import type { Attachment } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { isOfType, parseReferenceString } from '@seen/core/utils'
import { ImageZoom } from '@seen/ui'
import { IconAlertTriangle } from '@tabler/icons-react'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import type { JSX } from 'react'
import { AttachmentCard } from '#src/components/attachments/AttachmentCard.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { useAttachmentConversionStatus } from '#src/hooks/useAttachmentConversionStatus.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'

function ImageAttachment({
  attachment,
}: {
  attachment: Attachment
}): JSX.Element {
  const { status } = useAttachmentConversionStatus(attachment)
  const stableUrl = useStableAttachmentUrl(attachment.url)

  if (status === 'pending') {
    return (
      <div className="relative h-32 w-full">
        <Skeleton
          role="status"
          aria-label="File converting"
          className="h-full w-full"
        />
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Converting...
        </p>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-2 py-4 text-sm text-muted-foreground"
      >
        <IconAlertTriangle className="h-6 w-6 text-amber-500" />
        <p>Preview unavailable — this file couldn&apos;t be converted</p>
      </div>
    )
  }

  return (
    <ImageZoom>
      <img src={stableUrl ?? attachment.url} alt={attachment.title} />
    </ImageZoom>
  )
}

export function DocumentReferenceNodeComponent(
  props: NodeViewProps
): JSX.Element {
  const referenceString = props.node.attrs.reference as string | null
  const displayString = props.node.attrs.display as string | null
  const reference = parseReferenceString(referenceString, displayString)
  invariant(
    isOfType(reference, 'DocumentReference'),
    'Invalid document reference'
  )
  const { data: resource } = useRead(ExtendedDocumentReference, reference)

  const attachments = resource?.attachments ?? []

  return (
    <>
      {attachments.map((attachment) => (
        <NodeViewWrapper key={attachment.id}>
          {isBrowserSupportedImage(attachment.contentType) ? (
            <ImageAttachment attachment={attachment} />
          ) : (
            <AttachmentCard attachment={attachment} />
          )}
        </NodeViewWrapper>
      ))}
    </>
  )
}

function isBrowserSupportedImage(contentType: string | undefined): boolean {
  if (!contentType) {
    return false
  }

  if (supportedImageTypes.has(contentType)) {
    return true
  }

  if (safariSupportedOnly.has(contentType)) {
    return isSafariOnAppleDevice()
  }

  return false
}

const supportedImageTypes = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/webp',
  'image/avif',
  'image/svg+xml',
  'image/x-icon',
])

const safariSupportedOnly = new Set(['image/heic'])

function isSafariOnAppleDevice(): boolean {
  const ua = navigator.userAgent
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
  const isApple = /Macintosh|iPhone|iPad|iPod/.test(ua)
  return isSafari && isApple
}
